import { useState, useEffect } from "react";
import {
  Play, Check, ArrowRight, Star, BadgeCheck, Globe, Plane, Ship, Hotel,
  Gift, Sparkles, ShieldCheck, Users, TrendingUp, ChevronDown, Phone,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1612225593718-6370b31a69b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const START_VIDEO = "2tVxGpbkcD4";
const END_VIDEO = "fPdIZw89XkI";
const CTA_PRIMARY = "/plandeagencias";
const CTA_PROMO = "/promo";

/* ---------- Video con facade (lazy) ---------- */
const VideoEmbed = ({ id, title }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="relative w-full rounded-[22px] overflow-hidden shadow-[0_22px_60px_rgba(0,21,63,0.28)] bg-black" style={{ aspectRatio: "16 / 9" }}>
      {playing ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title={title} frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen data-testid={`demo-video-${id}`}
        />
      ) : (
        <button onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full" aria-label="Reproducir video" data-testid={`demo-video-play-${id}`}>
          <img src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`} alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`; }} />
          <span className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-[#FF6B4A] shadow-[0_8px_30px_rgba(255,107,74,.6)] flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="h-9 w-9 text-white ml-1" fill="white" />
          </span>
        </button>
      )}
    </div>
  );
};

const BENEFITS = [
  { t: "Hasta 80% de comisión", c: "#80C753" },
  { t: "Hasta 70% de descuento en hoteles, cruceros y más", c: "#1E9BE0" },
  { t: "Capacitación + Carnet de Agente de Viajes", c: "#F5B700" },
  { t: "Herramientas de reserva y tu propia web", c: "#17C3B2" },
  { t: "Ingresos ilimitados por bonos de referidos", c: "#FF6B4A" },
];

const STATS = [
  { n: "+6,000", l: "Agencias registradas", c: "#1E9BE0" },
  { n: "50+", l: "Países", c: "#80C753" },
  { n: "12+", l: "Años de experiencia", c: "#F5B700" },
  { n: "3.3M", l: "Hoteles disponibles", c: "#FF6B4A" },
];

const STEPS = [
  { n: "1", t: "Regístrate", d: "Abre tu propia agencia de viajes digital en minutos, sin oficina ni experiencia.", Icon: BadgeCheck, c: "#1E9BE0" },
  { n: "2", t: "Capacítate", d: "Accede a la academia, tus plataformas y tu carnet de agente. Te damos los clientes.", Icon: Users, c: "#80C753" },
  { n: "3", t: "Gana", d: "Vende viajes y experiencias con precios de agencia y cobra tus comisiones.", Icon: TrendingUp, c: "#F5B700" },
];

const BRANDS = [
  { name: "Booking.com", file: "/brands/booking.svg" },
  { name: "Expedia", file: "/brands/expedia.svg" },
  { name: "Marriott", file: "/brands/marriott.svg" },
  { name: "Hilton", file: "/brands/hilton.svg" },
  { name: "Hyatt", file: "/brands/hyatt.svg" },
  { name: "Wyndham", file: "/brands/wyndham.svg" },
  { name: "IHG", file: "/brands/ihg.svg" },
  { name: "Disney", file: "/brands/disney.png" },
  { name: "Royal Caribbean", file: "/brands/royalcaribbean.svg" },
  { name: "Norwegian Cruise Line", file: "/brands/norwegian.svg" },
];

const BrandLogo = ({ b }) => {
  const [err, setErr] = useState(false);
  return (
    <div className="flex items-center justify-center h-20 sm:h-24 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all px-5" data-testid={`demo-brand-${b.name}`}>
      {err ? (
        <span className="font-extrabold text-sm sm:text-base text-center text-[#00153F]">{b.name}</span>
      ) : (
        <img src={b.file} alt={b.name} className="max-h-10 sm:max-h-12 max-w-[150px] w-auto object-contain" loading="lazy" onError={() => setErr(true)} />
      )}
    </div>
  );
};

const FAQS = [
  { q: "¿Necesito experiencia previa?", a: "No. Club Viajemos te da toda la capacitación, las plataformas y el acompañamiento para empezar desde cero." },
  { q: "¿Cómo ganan los agentes?", a: "Ganas comisiones por cada viaje, certificado, membresía o asesoría que vendas — hasta el 80% según tu nivel de agencia." },
  { q: "¿De dónde saco clientes?", a: "Empiezas con tu círculo cercano y además te enviamos clientes: tus primeros 100 van por nuestra cuenta al registrarte como agencia." },
  { q: "¿Qué incluye mi membresía de agencia?", a: "Tu propia agencia con marca y logo, acceso a 3.3M de hoteles y 330+ proveedores, carnet de agente, academia y tu sistema de clientes con IA." },
  { q: "¿Puedo trabajarlo desde casa?", a: "Sí. Todo funciona desde el celular, en tus tiempos, desde donde quieras." },
];

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden" data-testid={`demo-faq-${idx}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-4 text-left px-5 py-4">
        <span className="font-bold text-[#00153F] text-sm sm:text-base">{q}</span>
        <ChevronDown className={`h-5 w-5 text-[#1E9BE0] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 -mt-1 text-slate-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
};

const Testimonios = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch("/api/tutorials", { cache: "no-store" })
      .then(r => r.json())
      .then(d => {
        const yt = (u) => { const m = (u || "").match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/); return m ? m[1] : null; };
        const list = (d || []).filter(t => (t.location || "home") === "home" && /^CONOCE\s+A/i.test(t.title || "") && yt(t.url))
          .sort((a, b) => (a.order || 0) - (b.order || 0)).slice(0, 6)
          .map(t => ({ id: yt(t.url), url: t.url, name: (t.title || "").replace(/^CONOCE\s+A\s+/i, "").split(/[.\u2022]|\bCOMO\b/i)[0].trim() }));
        setItems(list);
      }).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
      {items.map(t => (
        <a key={t.id} href={t.url} target="_blank" rel="noopener noreferrer" className="group rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`demo-testimonio-${t.id}`}>
          <div className="relative aspect-video bg-black" style={{ backgroundImage: `url(https://img.youtube.com/vi/${t.id}/hqdefault.jpg)`, backgroundSize: "cover", backgroundPosition: "center" }}>
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-12 h-12 rounded-full bg-[#FF6B4A]/90 flex items-center justify-center group-hover:scale-110 transition-transform"><Play className="h-5 w-5 text-white ml-0.5" fill="white" /></span>
            </span>
          </div>
          <p className="px-3 py-2.5 font-bold text-[#00153F] text-sm capitalize">{t.name.toLowerCase()}</p>
        </a>
      ))}
    </div>
  );
};

export default function HomeDemoPage() {
  return (
    <div className="min-h-screen bg-white text-[#00153F]">
      {/* DEMO ribbon */}
      <div className="bg-[#00153F] text-white text-center text-[12px] font-bold py-2 px-4" data-testid="demo-ribbon">
        VISTA PREVIA / DEMO · Nuevo diseño del Home — <a href="/" className="underline decoration-[#F5B700] underline-offset-2">ver home actual</a>
      </div>

      {/* Promo strip */}
      <div className="bg-gradient-to-r from-[#FF6B4A] via-[#F5B700] to-[#FF6B4A] text-[#00153F] text-center text-sm sm:text-base font-black py-2.5 px-4 flex items-center justify-center gap-2">
        <Gift className="h-4 w-4" /> Tus primeros 100 clientes van por nuestra cuenta al registrarte como agencia
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="demo-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#06A6E0] via-[#1E9BE0] to-[#17C3B2]" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F5B700]/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#80C753]/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-white">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 px-4 py-1.5 text-xs font-black uppercase tracking-wide mb-5">
              <Globe className="h-4 w-4" /> Club Viajemos · Travel Empire
            </span>
            <h1 className="font-black leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
              Conviértete en <span className="text-[#F5E08A]">Agente de Viajes</span> Independiente
            </h1>
            <p className="text-white/95 text-lg sm:text-xl font-semibold mb-7 max-w-xl">
              Ahorra en tus viajes, genera ingresos extra o construye tu propio negocio — <span className="underline decoration-[#F5B700] decoration-4 underline-offset-2">tú decides</span>.
            </p>
            <ul className="space-y-2.5 mb-8">
              {BENEFITS.map(b => (
                <li key={b.t} className="flex items-center gap-3 text-white font-semibold">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: b.c }}><Check className="h-4 w-4 text-white" /></span>
                  {b.t}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={CTA_PRIMARY} data-testid="demo-cta-primary" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B4A] hover:bg-[#ff5436] text-white font-black text-base px-7 py-4 shadow-[0_12px_34px_rgba(255,107,74,.5)] transition-all hover:-translate-y-0.5">
                Quiero mi agencia <ArrowRight className="h-5 w-5" />
              </a>
              <a href={CTA_PROMO} data-testid="demo-cta-secondary" className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-white/90 text-[#00153F] font-black text-base px-7 py-4 transition-all hover:-translate-y-0.5">
                Ver precios de hoy
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 bg-white/20 rounded-[32px] blur-xl" />
            <img src={HERO_IMG} alt="Viaja y gana con Club Viajemos" className="relative rounded-[28px] shadow-2xl w-full h-[340px] sm:h-[420px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 text-[#F5B700]" fill="#F5B700" />)}</div>
              <span className="text-xs font-bold text-[#00153F]">+6,000 agencias felices</span>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO INICIO */}
      <section className="bg-[#F6F9FF] py-14 sm:py-20 px-4" data-testid="demo-video-inicio">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#1E9BE0]/10 text-[#1E9BE0] px-4 py-1.5 text-xs font-black uppercase tracking-wide mb-4"><Sparkles className="h-4 w-4" /> Míralo en 3 minutos</span>
          <h2 className="font-black text-3xl sm:text-4xl lg:text-5xl mb-3" style={{ fontFamily: "Poppins, sans-serif" }}>Así funciona Club Viajemos</h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto mb-9">Descubre cómo abres tu agencia, consigues precios de agencia y empiezas a ganar.</p>
          <div className="max-w-[900px] mx-auto"><VideoEmbed id={START_VIDEO} title="Club Viajemos — Intro" /></div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="demo-stats">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map(s => (
            <div key={s.l} className="rounded-2xl p-6 text-center text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${s.c}, ${s.c}cc)` }}>
              <p className="text-3xl sm:text-4xl font-black leading-none mb-1">{s.n}</p>
              <p className="text-xs sm:text-sm font-semibold opacity-95">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ID CARD */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10 items-center" data-testid="demo-idcard">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#F5B700]/15 text-[#b07d00] px-4 py-1.5 text-xs font-black uppercase tracking-wide mb-4"><BadgeCheck className="h-4 w-4" /> Credencial oficial</span>
          <h2 className="font-black text-3xl sm:text-4xl mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>Tu Carnet de Agente de Viajes</h2>
          <p className="text-slate-600 text-base leading-relaxed mb-5">Desde el primer día recibes tu credencial de Agente de Viajes Internacional, respaldada por <strong>Florida Global University</strong> y la licencia <strong>Seller of Travel ST39568</strong>. Preséntate como el profesional que eres.</p>
          <ul className="space-y-2">
            {["Capacitación completa en la academia", "Tu propia web y herramientas de reserva", "Acceso a 3.3M de hoteles con precio de agencia"].map(t => (
              <li key={t} className="flex items-center gap-3 font-semibold text-[#00153F]"><span className="w-5 h-5 rounded-full bg-[#80C753] flex items-center justify-center"><Check className="h-3.5 w-3.5 text-white" /></span>{t}</li>
            ))}
          </ul>
        </div>
        <div className="flex justify-center">
          <div className="relative w-full max-w-sm rounded-3xl p-6 text-white shadow-2xl overflow-hidden" style={{ background: "linear-gradient(135deg,#00153F,#06264f 55%,#1E9BE0)" }}>
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#F5B700]/25 blur-2xl" />
            <div className="relative flex items-center justify-between mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[3px] text-[#F5E08A] font-black">Travel Empire</p>
                <p className="text-sm font-bold">Agente de Viajes Internacional</p>
              </div>
              <Globe className="h-8 w-8 text-[#45A0EE]" />
            </div>
            <div className="relative flex items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center">
                <Users className="h-9 w-9 text-white/80" />
              </div>
              <div>
                <p className="text-lg font-black leading-tight">Tu Nombre</p>
                <p className="text-xs text-white/70">Club Viajemos · Agencia</p>
                <p className="text-xs text-white/70 mt-1">ID: CV-2026-00000</p>
              </div>
            </div>
            <div className="relative flex items-center justify-between text-[10px] text-white/80 border-t border-white/15 pt-3">
              <span>Seller of Travel · ST39568</span>
              <span className="inline-flex items-center gap-1 text-[#80C753] font-bold"><ShieldCheck className="h-3.5 w-3.5" /> Verificado</span>
            </div>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="bg-[#F6F9FF] py-16 px-4" data-testid="demo-steps">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="font-black text-3xl sm:text-4xl mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>Cómo funciona</h2>
          <p className="text-slate-500 text-lg mb-10">Es tan fácil como 1 · 2 · 3</p>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map(s => (
              <div key={s.n} className="relative rounded-3xl bg-white border border-slate-100 shadow-sm p-7 text-left hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${s.c}1a` }}>
                  <s.Icon className="h-7 w-7" style={{ color: s.c }} />
                </div>
                <span className="absolute top-6 right-6 text-5xl font-black opacity-10" style={{ color: s.c }}>{s.n}</span>
                <h3 className="font-black text-xl mb-2 text-[#00153F]">{s.t}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <a href={CTA_PRIMARY} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1E9BE0] hover:bg-[#0f88cd] text-white font-black text-base px-8 py-4 mt-10 shadow-lg transition-all hover:-translate-y-0.5" data-testid="demo-steps-cta">
            Empieza hoy <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="demo-testimonios">
        <div className="text-center mb-10">
          <h2 className="font-black text-3xl sm:text-4xl mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>Escucha a nuestros agentes</h2>
          <p className="text-slate-500 text-lg">No te quedes solo con nuestra palabra.</p>
        </div>
        <Testimonios />
      </section>

      {/* BRANDS */}
      <section className="bg-gradient-to-b from-white to-[#F6F9FF] py-16 px-4" data-testid="demo-brands">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3 text-[#1E9BE0]">
            <Plane className="h-6 w-6" /><Hotel className="h-6 w-6" /><Ship className="h-6 w-6" />
          </div>
          <h2 className="font-black text-3xl sm:text-4xl mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>
            Trabajamos con las compañías de viajes<br className="hidden sm:block" /> más grandes del mundo
          </h2>
          <p className="text-slate-500 text-base mb-10 max-w-2xl mx-auto">Acceso directo al mismo inventario y tarifas que usan las agencias profesionales más grandes del planeta.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {BRANDS.map((b) => <BrandLogo key={b.name} b={b} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="demo-faq-section">
        <div className="text-center mb-10">
          <h2 className="font-black text-3xl sm:text-4xl mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>¿Tienes preguntas?</h2>
          <p className="text-slate-500 text-lg">Estamos aquí para ayudarte.</p>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => <FaqItem key={i} idx={i} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* VIDEO FINAL */}
      <section className="bg-[#00153F] py-16 px-4" data-testid="demo-video-final">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white px-4 py-1.5 text-xs font-black uppercase tracking-wide mb-4"><Play className="h-4 w-4" /> Para cerrar</span>
          <h2 className="font-black text-3xl sm:text-4xl text-white mb-3" style={{ fontFamily: "Poppins, sans-serif" }}>Una última historia que te va a inspirar</h2>
          <p className="text-white/70 text-base max-w-2xl mx-auto mb-9">Mira por qué miles ya dieron el paso con Club Viajemos.</p>
          <div className="max-w-[900px] mx-auto"><VideoEmbed id={END_VIDEO} title="Club Viajemos — Historia" /></div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden py-16 px-4" data-testid="demo-cta-final">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B4A] via-[#F5B700] to-[#80C753]" />
        <div className="relative max-w-3xl mx-auto text-center text-[#00153F]">
          <h2 className="font-black text-3xl sm:text-5xl mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>Abre tu agencia de viajes hoy</h2>
          <p className="text-[#00153F]/90 text-lg font-semibold mb-8">Con tu marca y tu logo, lista en un solo día. Nosotros ponemos las plataformas, los clientes y la capacitación.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={CTA_PRIMARY} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#00153F] hover:bg-[#06264f] text-white font-black text-base px-8 py-4 shadow-xl transition-all hover:-translate-y-0.5" data-testid="demo-cta-final-btn">
              Quiero mi agencia <ArrowRight className="h-5 w-5" />
            </a>
            <a href="tel:+19548059212" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#00153F] font-black text-base px-8 py-4 shadow-xl transition-all hover:-translate-y-0.5">
              <Phone className="h-5 w-5" /> Habla con un experto
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-[#00153F] text-white/70 text-center text-sm py-8 px-4">
        Club<span className="text-[#45A0EE] font-bold">Viajemos</span> · © 2026 Travel Empire · Seller of Travel ST39568
      </footer>
    </div>
  );
}
